/**
 * Gestionnaire de consentement cookies — MGB School (CNIL / RGPD).
 * Aucun traceur non essentiel n'est chargé tant que l'utilisateur n'a pas opté explicitement pour la catégorie.
 * Catégories : essential (toujours actif) · analytics (PostHog sans enregistrement) · marketing (Meta Pixel) · recording (PostHog session replay).
 */
const CONSENT_KEY = "mgb_cookie_consent";
const CONSENT_VERSION = 2;
const CONSENT_TTL_MS = 6 * 30 * 24 * 60 * 60 * 1000; // 6 mois (recommandation CNIL)
const META_PIXEL_ID = "338077314135026";
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";
const REOPEN_EVENT = "mgb:reopen-cookie-banner";
const API = process.env.REACT_APP_BACKEND_URL;

export const CATEGORIES = [
  { id: "essential", label: "Strictement nécessaires", locked: true, description: "Session, authentification de l'espace apprenant, sécurité, paiement PayPal et mémorisation de votre choix cookies. Indispensables au fonctionnement du site." },
  { id: "analytics", label: "Statistiques / mesure d'audience", description: "PostHog — pages vues et parcours agrégés, sans enregistrement de session. Nous aide à améliorer le site." },
  { id: "marketing", label: "Publicité / marketing", description: "Meta Pixel (Facebook / Instagram) — mesure des conversions de nos campagnes publicitaires." },
  { id: "recording", label: "Enregistrement de session / heatmaps", description: "PostHog Session Replay — rejoue anonymement votre navigation pour détecter les blocages d'ergonomie. Désactivé par défaut." },
];

export const DEFAULT_PREFS = { essential: true, analytics: false, marketing: false, recording: false };

export const getStoredConsent = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(CONSENT_KEY) || "null");
    if (!parsed || typeof parsed !== "object" || parsed.version !== CONSENT_VERSION) return null;
    if (!parsed.timestamp || Date.now() - parsed.timestamp > CONSENT_TTL_MS) { localStorage.removeItem(CONSENT_KEY); return null; }
    return parsed;
  } catch { return null; }
};

export const hasAnalyticsConsent = () => !!getStoredConsent()?.prefs?.marketing;

const loadScript = (src) => new Promise((resolve, reject) => {
  const s = document.createElement("script");
  s.async = true; s.src = src; s.onload = resolve; s.onerror = reject;
  document.head.appendChild(s);
});

const enableMetaPixel = async () => {
  if (window.__mgbPixelLoaded) return;
  window.__mgbPixelLoaded = true;
  if (!window.fbq) {
    const n = (window.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); });
    window._fbq = n; n.push = n; n.loaded = false; n.version = "2.0"; n.queue = [];
  }
  try {
    await loadScript("https://connect.facebook.net/en_US/fbevents.js");
    window.fbq("init", META_PIXEL_ID);
    window.fbq("track", "PageView");
  } catch (err) { console.warn("[Meta Pixel] init failed", err); }
};

const enablePostHog = async (recording) => {
  if (!POSTHOG_KEY) return;
  if (window.__mgbPostHogLoaded) {
    if (window.posthog?.startSessionRecording && recording) window.posthog.startSessionRecording();
    return;
  }
  window.__mgbPostHogLoaded = true;
  try {
    const mod = await import("posthog-js");
    const posthog = mod.default || mod;
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: "identified_only",
      disable_session_recording: !recording,
      capture_performance: false,
      capture_dead_clicks: false,
      disable_surveys: true,
      enable_heatmaps: recording,
      autocapture: true,
      opt_out_capturing_by_default: false,
    });
    window.posthog = posthog;
  } catch (err) { console.warn("[PostHog] init failed", err); }
};

const purgeTrackingCookies = () => {
  const hosts = [window.location.hostname, "." + window.location.hostname.split(".").slice(-2).join(".")];
  document.cookie.split(";").forEach((c) => {
    const name = c.split("=")[0].trim();
    if (/^(ph_|_fbp|_fbc|_ga|_gid)/.test(name)) {
      hosts.forEach((h) => { document.cookie = `${name}=; Max-Age=0; path=/; domain=${h}`; });
      document.cookie = `${name}=; Max-Age=0; path=/`;
    }
  });
  Object.keys(localStorage).forEach((k) => { if (k.startsWith("ph_")) localStorage.removeItem(k); });
};

const applyPrefs = (prefs) => {
  if (prefs.analytics || prefs.recording) enablePostHog(!!prefs.recording);
  if (prefs.marketing) enableMetaPixel();
};

const logConsent = (record) => {
  if (!API) return;
  fetch(`${API}/api/consent`, {
    method: "POST", headers: { "Content-Type": "application/json" }, keepalive: true,
    body: JSON.stringify({ consent_id: record.id, prefs: record.prefs, version: record.version, action: record.action, page: window.location.pathname }),
  }).catch(() => {});
};

/** Enregistre le choix. action = "accept_all" | "reject_all" | "custom". */
export const saveConsent = (prefs, action) => {
  const previous = getStoredConsent();
  const full = { ...DEFAULT_PREFS, ...prefs, essential: true };
  const record = { id: previous?.id || (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())), prefs: full, action, timestamp: Date.now(), version: CONSENT_VERSION };
  try { localStorage.setItem(CONSENT_KEY, JSON.stringify(record)); } catch { /* ignore */ }
  logConsent(record);
  const downgraded = ["analytics", "marketing", "recording"].some((k) => previous?.prefs?.[k] && !full[k]) || (!previous && (window.__mgbPostHogLoaded || window.__mgbPixelLoaded));
  if (downgraded) {
    try { window.posthog?.opt_out_capturing?.(); window.posthog?.stopSessionRecording?.(); } catch { /* ignore */ }
    purgeTrackingCookies();
    window.location.reload();
    return;
  }
  applyPrefs(full);
};

export const grantConsent = (level) => saveConsent(level === "all" ? { analytics: true, marketing: true, recording: false } : DEFAULT_PREFS, level === "all" ? "accept_all" : "reject_all");

export const reopenCookieBanner = () => window.dispatchEvent(new CustomEvent(REOPEN_EVENT));

export const onReopenRequest = (handler) => {
  window.addEventListener(REOPEN_EVENT, handler);
  return () => window.removeEventListener(REOPEN_EVENT, handler);
};

/** Au montage : applique le consentement déjà donné ; sinon, rien n'est chargé. */
export const bootstrapConsent = () => {
  purgeIfNoConsent();
  const c = getStoredConsent();
  if (c) applyPrefs(c.prefs);
};

const purgeIfNoConsent = () => {
  const c = getStoredConsent();
  if (!c || (!c.prefs.analytics && !c.prefs.recording && !c.prefs.marketing)) purgeTrackingCookies();
};
